import { useEffect } from 'react';

const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION || 'dev';
const VERSION_CHECK_INTERVAL = 30 * 60 * 1000; // 30 minutes

/**
 * Periodically checks for new app versions and auto-refreshes
 * Prevents users from running outdated code with mismatched chunks
 * Only runs in production builds to avoid false positives in development
 */
export function useVersionCheck() {
  useEffect(() => {
    // ONLY run in production builds
    if (import.meta.env.DEV || CURRENT_VERSION === 'dev') {
      console.log('[VERSION_CHECK] Skipping in development mode');
      return;
    }

    const checkVersion = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now(), { 
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' }
        });
        
        if (!response.ok) return;
        
        const { version } = await response.json();
        
        if (version && version !== CURRENT_VERSION) {
          // Only reload if versions are actually different timestamps
          const versionNum = parseInt(version);
          const currentNum = parseInt(CURRENT_VERSION);
          
          // Skip if either is not a valid number
          if (isNaN(versionNum) || isNaN(currentNum)) {
            console.log('[VERSION_CHECK] Skipping - invalid version format');
            return;
          }
          
          // Only reload if version difference is significant (> 1 minute)
          const timeDiff = Math.abs(versionNum - currentNum);
          if (timeDiff < 60000) {
            console.log('[VERSION_CHECK] Skipping - trivial version difference');
            return;
          }
          
          console.log('[VERSION_CHECK] New version detected, reloading...', {
            current: CURRENT_VERSION,
            latest: version,
            timeDiff: `${Math.round(timeDiff / 1000)}s`
          });
          
          // Show a brief message before reloading
          const notice = document.createElement('div');
          notice.style.cssText = 'position:fixed;top:20px;right:20px;background:#000;color:#fff;padding:16px 24px;border-radius:8px;z-index:9999;';
          notice.textContent = 'New version available, updating...';
          document.body.appendChild(notice);
          
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      } catch (error) {
        console.error('[VERSION_CHECK] Failed:', error);
      }
    };

    // Check immediately on mount
    checkVersion();
    
    // Then check periodically
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    
    return () => clearInterval(interval);
  }, []);
}
